import { useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowLeft, CalendarPlus, ChevronDown, ChevronUp, Eye, FileText, Pause, Pencil, Play, Plus, RotateCcw } from "lucide-react";
import { API, api, formatApiError } from "../api";
import { OWNER_MEMBERS } from "../constants/testIds";
import { formatCurrency } from "../lib/format";
import { fadeUp } from "../lib/motion";
import { Button } from "../components/ui/button";
import { StatusPill } from "../components/common/StatusPill";
import { ErrorState } from "../components/common/ErrorState";
import { GymLoader } from "../components/common/GymLoader";
import { EditMemberModal } from "../components/desk/EditMemberModal";
import { ReactivateModal } from "../components/desk/ReactivateModal";
import { RenewModal } from "../components/desk/RenewModal";
import { AddPlanModal } from "../components/desk/AddPlanModal";
import { MemberProfileSection } from "../components/common/MemberProfileSection";
import { BookMemberIntoClassDialog } from "../components/scheduling/BookMemberIntoClassDialog";
import { toast } from "../components/ui/sonner";

// Owner Cockpit's member detail — the drill-down target from the new Members list. Reuses the
// EXACT same backend-wired modals already built + tested for the Desk shell's MemberDetail.jsx
// (EditMemberModal/ReactivateModal/RenewModal/AddPlanModal have zero DeskDataContext coupling —
// verified before building this), and mirrors its query keys (`desk-member*`) so those modals'
// own `invalidateQueries` calls correctly refresh this page's data too, with zero backend or
// shared-component changes. "Add family member" intentionally omitted here — that flow needs a
// specific branch_id, which only the branch-scoped Desk shell has; Desk staff still own it.
export default function MemberDetail() {
  const { memberId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const reducedMotion = useReducedMotion();
  const queryClient = useQueryClient();
  const [modal, setModal] = useState(null); // "edit" | "reactivate" | "renew" | "addPlan"
  const [activeMembership, setActiveMembership] = useState(null);
  const [historyOpen, setHistoryOpen] = useState(false);

  const memberQ = useQuery({
    queryKey: ["desk-member", memberId],
    queryFn: async () => (await api.get(`/members/${memberId}`)).data,
  });

  const balanceQ = useQuery({
    queryKey: ["desk-member-balance", memberId],
    queryFn: async () => (await api.get(`/members/${memberId}/balance`)).data,
  });

  const ledgerQ = useQuery({
    queryKey: ["desk-member-ledger", memberId],
    queryFn: async () => (await api.get("/ledger", { params: { member_ref: memberId } })).data.entries,
  });

  const membershipsQ = useQuery({
    queryKey: ["desk-member-memberships", memberId],
    queryFn: async () => (await api.get(`/members/${memberId}/memberships`)).data.memberships,
  });

  const invoicesQ = useQuery({
    queryKey: ["desk-member-invoices", memberId],
    queryFn: async () => (await api.get("/invoices", { params: { member_ref: memberId } })).data.invoices,
  });
  const invoiceByPayment = useMemo(
    () => Object.fromEntries((invoicesQ.data || []).filter((i) => i.payment_ref).map((i) => [i.payment_ref, i])),
    [invoicesQ.data],
  );

  const generateInvoiceMutation = useMutation({
    mutationFn: (paymentRef) => api.post("/invoices/for-payment", { payment_ref: paymentRef }),
    onSuccess: (res) => {
      window.open(`${API}/invoices/${res.data.id}/pdf`, "_blank");
      queryClient.invalidateQueries({ queryKey: ["desk-member-invoices", memberId] });
    },
    onError: (e) => toast.error(formatApiError(e).message),
  });

  const plansQ = useQuery({ queryKey: ["desk-plans"], queryFn: async () => (await api.get("/plans")).data.plans });
  const planById = useMemo(() => Object.fromEntries((plansQ.data || []).map((p) => [p.id, p])), [plansQ.data]);

  // Header "latest invoice" action — the member's most recent payment, re-download if it already
  // has an invoice, generate-on-demand if it doesn't (reuses the exact same mutation/lookup the
  // ledger rows below use, so there's one source of truth for "does this payment have an
  // invoice yet").
  const latestPaymentRef = useMemo(() => {
    const payments = (ledgerQ.data || []).filter((e) => e.related_type === "payment" && e.direction === "credit" && e.related_ref);
    return payments.length ? payments[payments.length - 1].related_ref : null;
  }, [ledgerQ.data]);
  const latestInvoice = latestPaymentRef ? invoiceByPayment[latestPaymentRef] : null;

  const { current, history } = useMemo(() => {
    const all = membershipsQ.data || [];
    const latestByPlan = new Map();
    for (const m of all) {
      if (m.status !== "active" && m.status !== "frozen") continue;
      const existing = latestByPlan.get(m.plan_ref);
      if (!existing || m.state.start > existing.state.start) latestByPlan.set(m.plan_ref, m);
    }
    const currentList = Array.from(latestByPlan.values()).sort((a, b) => (a.state.start < b.state.start ? 1 : -1));
    const currentIds = new Set(currentList.map((m) => m.id));
    const historyList = all.filter((m) => !currentIds.has(m.id));
    return { current: currentList, history: historyList };
  }, [membershipsQ.data]);

  // DEFECT-D-18: `?renew=1` (from a search-result quick action) auto-opens Renew on this
  // member's most current membership — the same "fewer steps" shortcut the reactivate query
  // param already gave the Desk shell's own MemberDetail page.
  useEffect(() => {
    if (searchParams.get("renew") === "1" && current.length > 0) {
      setActiveMembership(current[0]);
      setModal("renew");
      searchParams.delete("renew");
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, current, setSearchParams]);

  const freezeMutation = useMutation({
    mutationFn: (m) => api.post(`/memberships/${m.id}/freeze`, { expected_version: m._sync.version }, { headers: { "Idempotency-Key": crypto.randomUUID() } }),
    onSuccess: () => {
      toast.success("Membership frozen.");
      queryClient.invalidateQueries({ queryKey: ["desk-member-memberships", memberId] });
    },
    onError: (e) => toast.error(formatApiError(e).message),
  });

  const resumeMutation = useMutation({
    mutationFn: (m) => api.post(`/memberships/${m.id}/resume`, { expected_version: m._sync.version }, { headers: { "Idempotency-Key": crypto.randomUUID() } }),
    onSuccess: () => {
      toast.success("Membership resumed.");
      queryClient.invalidateQueries({ queryKey: ["desk-member-memberships", memberId] });
    },
    onError: (e) => toast.error(formatApiError(e).message),
  });

  if (memberQ.isError) return <ErrorState error={memberQ.error} onRetry={() => memberQ.refetch()} title="Couldn't load this member" />;
  if (memberQ.isLoading) return <GymLoader fullPage label="Loading member…" />;

  const member = memberQ.data;
  const isLapsed = member.status !== "active" || current.length === 0;

  return (
    <div data-testid={OWNER_MEMBERS.detailPage} className="space-y-5 max-w-3xl">
      <Link to="/app/members" data-testid={OWNER_MEMBERS.detailBackLink} className="inline-flex items-center gap-1.5 text-sm font-medium scarlet-focusable hover:opacity-70" style={{ color: "var(--scarlet-text-secondary)" }}>
        <ArrowLeft size={14} /> Back to Members
      </Link>

      <motion.div {...fadeUp(reducedMotion)} className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2" style={{ color: "var(--scarlet-text)" }}>
            {member.profile.name}
            <StatusPill status={member.status === "active" ? "active" : "overdue"} label={member.status === "active" ? "Active" : "Lapsed"} />
          </h1>
          <p className="text-sm mt-0.5" style={{ color: "var(--scarlet-text-secondary)" }}>{member.profile.phone} {member.profile.email ? `· ${member.profile.email}` : ""}</p>
        </div>
        <Button variant="outline" size="sm" data-testid={OWNER_MEMBERS.editButton} onClick={() => setModal("edit")} className="gap-1.5"><Pencil size={13} /> Edit</Button>
      </motion.div>

      <motion.div {...fadeUp(reducedMotion, 0.015)}>
        <Button variant="outline" size="sm" data-testid={OWNER_MEMBERS.bookClassButton} onClick={() => setModal("bookClass")} className="gap-1.5">
          <CalendarPlus size={13} /> Book into a class
        </Button>
      </motion.div>

      <motion.div {...fadeUp(reducedMotion, 0.03)}>
        <MemberProfileSection member={member} idPrefix="owner-member" />
      </motion.div>

      {latestPaymentRef && (
        <motion.div {...fadeUp(reducedMotion, 0.02)} className="flex gap-2">
          {latestInvoice ? (
            <>
              <Button
                variant="outline" size="sm" data-testid={`${OWNER_MEMBERS.latestInvoiceButton}-show`}
                onClick={() => window.open(`${API}/invoices/${latestInvoice.id}/pdf?inline=true`, "_blank")}
                className="gap-1.5"
              >
                <Eye size={13} /> Show invoice
              </Button>
              <Button
                variant="outline" size="sm" data-testid={OWNER_MEMBERS.latestInvoiceButton}
                onClick={() => window.open(`${API}/invoices/${latestInvoice.id}/pdf`, "_blank")}
                className="gap-1.5"
              >
                <FileText size={13} /> Re-download latest invoice
              </Button>
            </>
          ) : (
            <Button
              variant="outline" size="sm" data-testid={OWNER_MEMBERS.latestInvoiceButton}
              disabled={generateInvoiceMutation.isPending}
              onClick={() => generateInvoiceMutation.mutate(latestPaymentRef)}
              className="gap-1.5"
            >
              <FileText size={13} /> Generate latest invoice
            </Button>
          )}
        </motion.div>
      )}

      <motion.div {...fadeUp(reducedMotion, 0.04)} className="grid grid-cols-2 gap-3">
        <div className="solid-surface rounded-[14px] p-4">
          <p className="eyebrow mb-1">Dues balance</p>
          <p className="text-xl font-bold" style={{ color: balanceQ.data?.dues_balance > 0 ? "var(--scarlet-neg)" : "var(--scarlet-text)" }}>
            {formatCurrency(balanceQ.data?.dues_balance ?? 0)}
          </p>
        </div>
        <div className="solid-surface rounded-[14px] p-4">
          <p className="eyebrow mb-1">Active plans</p>
          <p className="text-xl font-bold" style={{ color: "var(--scarlet-text)" }}>{current.length}</p>
        </div>
      </motion.div>

      <motion.div {...fadeUp(reducedMotion, 0.06)} className="space-y-2">
        <div className="flex items-center justify-between">
          <p className="eyebrow">Plans</p>
          <Button data-testid={OWNER_MEMBERS.addPlanButton} size="sm" variant="outline" onClick={() => setModal("addPlan")} className="gap-1.5"><Plus size={13} /> Add another plan</Button>
        </div>
        {isLapsed && (
          <div className="solid-surface rounded-[14px] p-4">
            <p className="text-sm mb-2" style={{ color: "var(--scarlet-text-secondary)" }}>No active plan on file.</p>
            <Button data-testid={OWNER_MEMBERS.reactivateOpenButton} onClick={() => setModal("reactivate")} className="text-white gap-1.5" style={{ background: "var(--scarlet-accent)" }}>
              <RotateCcw size={14} /> Reactivate
            </Button>
          </div>
        )}
        {current.map((m) => {
          const plan = planById[m.plan_ref];
          return (
            <div key={m.id} data-testid={OWNER_MEMBERS.membershipRow(m.id)} className="solid-surface rounded-[14px] p-4 flex items-center justify-between flex-wrap gap-2">
              <div>
                <p className="text-sm font-semibold flex items-center gap-2" style={{ color: "var(--scarlet-text)" }}>
                  {plan?.name || m.plan_ref}
                  <StatusPill status={m.status === "active" ? "active" : "overdue"} label={m.status} />
                  {m.discount_percent > 0 && <span className="status-pill">{m.discount_percent}% off</span>}
                </p>
                <p className="text-xs mt-0.5" style={{ color: "var(--scarlet-text-secondary)" }}>
                  {m.state.end ? `ends ${m.state.end}` : m.state.remaining_sessions != null ? `${m.state.remaining_sessions} sessions left` : "no expiry"}
                </p>
              </div>
              <div className="flex gap-2">
                {m.status === "active" && (
                  <Button size="sm" variant="outline" onClick={() => { setActiveMembership(m); setModal("renew"); }} data-testid={OWNER_MEMBERS.renewButton(m.id)}>Renew / change plan</Button>
                )}
                {m.status === "active" && (
                  <Button size="sm" variant="outline" onClick={() => freezeMutation.mutate(m)} disabled={freezeMutation.isPending} data-testid={OWNER_MEMBERS.freezeButton(m.id)} className="gap-1.5">
                    <Pause size={13} /> Freeze
                  </Button>
                )}
                {m.status === "frozen" && (
                  <Button size="sm" onClick={() => resumeMutation.mutate(m)} disabled={resumeMutation.isPending} data-testid={OWNER_MEMBERS.resumeButton(m.id)} className="text-white gap-1.5" style={{ background: "var(--scarlet-accent)" }}>
                    <Play size={13} /> Resume
                  </Button>
                )}
              </div>
            </div>
          );
        })}
        {history.length > 0 && (
          <div>
            <button onClick={() => setHistoryOpen((v) => !v)} className="flex items-center gap-1.5 text-xs font-medium mt-1" style={{ color: "var(--scarlet-text-secondary)" }}>
              {historyOpen ? <ChevronUp size={13} /> : <ChevronDown size={13} />} {history.length} older enrollment(s)
            </button>
            {historyOpen && (
              <div className="solid-surface rounded-[14px] overflow-hidden mt-2">
                {history.slice(0, 30).map((m) => (
                  <div key={m.id} className="flex items-center justify-between px-4 py-2 text-xs" style={{ borderTop: "1px solid var(--scarlet-hairline)", color: "var(--scarlet-text-secondary)" }}>
                    <span>{planById[m.plan_ref]?.name || m.plan_ref} · {m.state.start}</span>
                    <span className="status-pill">{m.status}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </motion.div>

      <motion.div {...fadeUp(reducedMotion, 0.1)}>
        <p className="eyebrow mb-2">Ledger history</p>
        <div data-testid={OWNER_MEMBERS.ledgerTable} className="solid-surface rounded-[14px] overflow-hidden">
          {(!ledgerQ.data || ledgerQ.data.length === 0) && <p className="p-4 text-sm" style={{ color: "var(--scarlet-text-secondary)" }}>No ledger entries yet.</p>}
          {ledgerQ.data?.slice().reverse().slice(0, 25).map((e) => {
            const isPayment = e.related_type === "payment" && e.direction === "credit" && !!e.related_ref;
            const invoice = isPayment ? invoiceByPayment[e.related_ref] : null;
            return (
              <div key={e._id} className="flex items-center justify-between px-4 py-2.5 text-sm" style={{ borderBottom: "1px solid var(--scarlet-hairline)" }}>
                <div>
                  <span className="font-medium" style={{ color: "var(--scarlet-text)" }}>{e.type.replace(/_/g, " ")}</span>
                  <span className="ml-2 text-xs" style={{ color: "var(--scarlet-text-secondary)" }}>{new Date(e.at).toLocaleDateString()}</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="font-semibold" style={{ color: e.direction === "credit" ? "var(--scarlet-pos)" : "var(--scarlet-neg)" }}>
                    {e.direction === "credit" ? "+" : "-"}{formatCurrency(e.amount)}
                  </span>
                  {isPayment && (
                    invoice ? (
                      <>
                        <button
                          onClick={() => window.open(`${API}/invoices/${invoice.id}/pdf?inline=true`, "_blank")}
                          className="text-xs font-semibold inline-flex items-center gap-1 hover:opacity-70"
                          style={{ color: "var(--scarlet-text)" }}
                        >
                          <Eye size={12} /> Show
                        </button>
                        <button
                          onClick={() => window.open(`${API}/invoices/${invoice.id}/pdf`, "_blank")}
                          className="text-xs font-semibold inline-flex items-center gap-1 hover:opacity-70"
                          style={{ color: "var(--scarlet-text)" }}
                        >
                          <FileText size={12} /> Invoice
                        </button>
                      </>
                    ) : (
                      <button
                        onClick={() => generateInvoiceMutation.mutate(e.related_ref)}
                        disabled={generateInvoiceMutation.isPending}
                        className="text-xs font-semibold inline-flex items-center gap-1 hover:opacity-70"
                        style={{ color: "var(--scarlet-accent)" }}
                      >
                        <FileText size={12} /> Generate invoice
                      </button>
                    )
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </motion.div>

      <EditMemberModal member={member} open={modal === "edit"} onOpenChange={(v) => setModal(v ? "edit" : null)} />
      <ReactivateModal member={member} open={modal === "reactivate"} onOpenChange={(v) => setModal(v ? "reactivate" : null)} />
      {activeMembership && <RenewModal member={member} membership={activeMembership} open={modal === "renew"} onOpenChange={(v) => setModal(v ? "renew" : null)} />}
      <AddPlanModal member={member} open={modal === "addPlan"} onOpenChange={(v) => setModal(v ? "addPlan" : null)} />
      <BookMemberIntoClassDialog
        open={modal === "bookClass"} onOpenChange={(v) => setModal(v ? "bookClass" : null)}
        memberId={memberId} memberName={member.profile.name} branchId={member.branch_id}
        testIds={{ dialog: OWNER_MEMBERS.bookClassDialog, select: OWNER_MEMBERS.bookClassSelect, confirm: OWNER_MEMBERS.bookClassConfirmButton }}
      />
    </div>
  );
}
